<template>
  <div id="shipper">
    <div class="container">

      <div class="page-title">
        <span>{{ $t("companies.settings.shipper") }}</span>
        <button class="save btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

      <Tabs>
        <TabSection title="Sandbox" v-if="hasShipperSandbox" collapsed collapsible>
          <ShipperSandbox :config="configData" :company-id="companyId" />
        </TabSection>

        <TabSection title="Configuration" collapsible>
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t("companies.settings.shipper") }}</label>
                <select v-model="shipper">
                  <option :value="null">{{ $t('none') }}</option>
                  <option value="captain_ai">Captain AI</option>
                  <option value="doordash">Doordash</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('activated') }}</label>
                <select v-model="activated">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>Generate tracking url</label>
                <select v-model="generateTrackingUrl">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
            </div>
          </div>
          <Panel title="Shipper parameters" v-if="shipper">
            <!-- Captain AI -->
            <template v-if="shipper === 'captain_ai'">
              <div class="columns">
                <div class="column">
                  <div class="group">
                    <label>Integration key</label>
                    <input type="text" v-model="credentials.captain_ai.integrationKey">
                  </div>
                  <div class="group">
                    <label>Developer key</label>
                    <input type="text" v-model="credentials.captain_ai.developerKey">
                  </div>
                </div>
                <div class="column">
                  <div class="group">
                    <label>Account UUID</label>
                    <input type="text" v-model="credentials.captain_ai.accountUUID">
                  </div>
                </div>
              </div>
              <br>
              <button class="save btn btn-outline" @click="test">Test shipper configuration</button>
            </template>
            <!-- DoorDash Drive -->
            <template v-if="shipper === 'doordash'">
              <div class="columns">
                <div class="column">
                  <div class="subtitle-2">Credentials</div>
                  <div class="group">
                    <label>Api key
                      <Tooltip text="Api key provided by doordash" />
                    </label>
                    <input type="text" v-model="credentials.doordash.api_key">
                  </div>
                  <div class="group">
                    <label>External business name
                      <Tooltip text="Legal name provided by doordash for invoicing, cannot change" />
                    </label>
                    <input type="text" v-model="credentials.doordash.external_business_name">
                  </div>
                  <br>
                  <div class="subtitle-2">Delivery Fees</div>
                  <TipBox info no-border>
                    <div v-html="$t('shipper.delivery_fees')"></div>
                  </TipBox>
                  <div class="group">
                    <input type="checkbox" id="chk_quickAdd" v-model="frontendConfig.deliveryFees.activated">
                    <label for="chk_quickAdd">Activate fixed delivery fee</label>
                  </div>
                  <div class="group"
                    :class="{ locked: !frontendConfig.deliveryFees || !frontendConfig.deliveryFees.activated }">
                    <label>Fixed delivery fee ({{ currencySymbol }})</label>
                    <input min="0" type="number" v-model="frontendConfig.deliveryFees.amount">
                  </div>
                  <TipBox info no-border>
                    <div v-html="$t('shipper.taxable_delivery_fees')"></div>
                  </TipBox>
                  <div class="group">
                    <input type="checkbox" id="chk_tax" v-model="frontendConfig.deliveryFees.nonTaxable">
                    <label for="chk_tax" v-html="$t('shipper.taxable_delivery_fees_label')"></label>
                  </div>
                </div>
                <div class="column">
                  <div class="subtitle-2">Pickup address information</div>
                  <div class="group">
                    <label>Pickup business name
                      <Tooltip text="The actual name of the company" />
                    </label>
                    <input type="text" v-model="credentials.doordash.pickup_business_name">
                  </div>
                  <div class="group">
                    <label>Pickup Street</label>
                    <input type="text" v-model="credentials.doordash.street">
                  </div>
                  <div class="group">
                    <label>Pickup City</label>
                    <input type="text" v-model="credentials.doordash.city">
                  </div>
                  <div class="group">
                    <label>Pickup State</label>
                    <input type="text" v-model="credentials.doordash.state">
                  </div>
                  <div class="group">
                    <label>Pickup unit/apt/suite</label>
                    <input type="text" v-model="credentials.doordash.unit">
                  </div>
                  <div class="group">
                    <label>Pickup Zip code</label>
                    <input type="text" v-model="credentials.doordash.zip_code">
                  </div>
                  <div class="group">
                    <label>Pickup instructions</label>
                    <input type="text" v-model="credentials.doordash.instructions">
                  </div>
                  <div class="group">
                    <label>Pickup phone number</label>
                    <input type="text" v-model="credentials.doordash.phone_number">
                  </div>
                </div>
              </div>
              <button class="save btn btn-outline" @click="test" :disabled="!this.hasMandatoryDoordashField">Test shipper
                configuration</button>
            </template>
          </Panel>
        </TabSection>
      </Tabs>

      <div class="footer-actions">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import Tooltip from "@/components/tools/Tooltip.vue";
  import ShipperSandbox from '@/components/shipper/ShipperSandbox.vue';
  import { mapActions } from "vuex";

  export default {
    name: "SettingsShipper",

    components: { ShipperSandbox, Tooltip },

    data() {
      return {
        companyId: null,
        branchId: null,
        shipper: null,
        activated: false,
        currencySymbol: "",
        generateTrackingUrl: false,
        frontendConfig: {
          deliveryFees: {
            activated: false,
            amount: null,
            nonTaxable: false,
          }
        },
        credentials: {
          captain_ai: {
            integrationKey: '',
            developerKey: '',
            accountUUID: '',
          },
          doordash: {
            api_key: '',
            external_business_name: '',
            pickup_business_name: '',
            street: '',
            city: '',
            state: '',
            unit: '',
            zip_code: '',
            phone_number: '',
            instructions: '',
            deliveryFees: null
          }
        }
      }
    },

    computed: {
      hasShipperSandbox() {
        return this.shipper === 'doordash'
      },
      configData() {
        return {
          shipper: this.shipper,
          activated: this.activated,
          generateTrackingUrl: this.generateTrackingUrl,
          frontendConfig: this.frontendConfig,
          credentials: this.shipper ? this.credentials[this.shipper] : null,
        }
      },
      hasMandatoryDoordashField() {
        return this.credentials.doordash.api_key && this.credentials.doordash.external_business_name && this.credentials.doordash.pickup_business_name;
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "settings.shipper";
      this.$store.state.pageIcon = "";

      this.companyId = this.$route.query.company;
      this.branchId = this.$route.query.branch;

      this.$store.commit('showSpinner', true);
      let response = await this.getCompany({ branchId: this.branchId, companyId: this.companyId, augment: 'shipper' });
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.$store.commit('showSpinner', false);

      this.applyConfig(response.shipperConfig);
    },

    methods: {
      ...mapActions(['getCompany', 'saveCompany']),

      async save() {
        if (this.shipper === 'doordash') {
          if (!this.hasMandatoryDoordashField) {
            toast(this.$t('shipper.error.missing_mantatory_doordash_fields'), 'error');
            return;
          } else if (this.frontendConfig.deliveryFees.activated && (this.frontendConfig.deliveryFees.amount == "" || this.frontendConfig.deliveryFees.amount == null)) {
            toast(this.$t('shipper.error.no_doordash_delivery_fees'), 'error');
            return;
          }
        }
        let data = {
          shipperConfig: this.configData
        };
        this.$store.commit('showSpinner', true);
        let response = await this.saveCompany({ branchId: this.branchId, companyId: this.companyId, augment: 'shipper', data });
        this.$store.commit('showSpinner', false);

        if (response) {
          toast(this.$t("company.success"), "success");
          this.applyConfig(response.shipperConfig);
        }
      },

      async test() {
        let data = this.configData;
        this.$store.commit('showSpinner', true);
        let response = await Core.request("POST", '/api/shipper-config-validate', data);
        this.$store.commit('showSpinner', false);

        if (response.valid) {
          toast('Valid', 'success');
        } else {
          toast('Invalid', 'error');
        }
      },

      applyConfig(config) {
        if (config) {
          this.shipper = config.shipper;
          this.activated = config.activated;
          if (config.frontendConfig) {
            if (config.frontendConfig.deliveryFees) {
              this.frontendConfig.deliveryFees.amount = parseFloat(config.frontendConfig.deliveryFees.amount) || 0;
              this.frontendConfig.deliveryFees.activated = config.frontendConfig.deliveryFees.activated || false;
              this.frontendConfig.deliveryFees.nonTaxable = config.frontendConfig.deliveryFees.nonTaxable || false;
            }
          }
          this.generateTrackingUrl = config.generateTrackingUrl;
          for (let [key, value] of Object.entries(config.credentials || {})) {
            this.credentials[this.shipper][key] = value
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  #shipper {
    display: flex;
  }
</style>